import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  Checkbox,
  Button,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { BillItem, KITCHEN_ITEM_STATUS } from '../../../all-types';
import { getKitchenItemStatusInfo } from '../../../utils';

export type ItemCardProps = {
  item: BillItem & { tableNo?: number; billNo?: string | number };
  selectable?: boolean;
  checked?: boolean;
  onCheckChange?: (checked: boolean) => void;
  onAdvance?: () => void;
  advanceLabel?: string; // NEW: custom label for the action button
};

export const ItemCard: React.FC<ItemCardProps> = ({
  item,
  selectable,
  checked,
  onCheckChange,
  onAdvance,
  advanceLabel = 'Next',
}) => {
  const sInfo = getKitchenItemStatusInfo(
    item?.kitchInfo?.status || KITCHEN_ITEM_STATUS.IN_KITCHEN
  );

  const instructions = (() => {
    const raw = item?.kitchInfo?.kitchenInstrcutions;
    if (!raw) return '';
    try {
      const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
      return Object.entries(obj)
        .map(([k, v]) => `${k}: ${String(v)}`)
        .join(', ');
    } catch {
      return String(raw);
    }
  })();

  return (
    <Card elevation={2} sx={{borderRadius: 3, backgroundColor: 'primary.light' }}>
      <CardContent>
        <Stack direction="row" alignItems="flex-start" spacing={1.25}>
          {selectable && (
            <Checkbox
              checked={!!checked}
              onChange={(e) => onCheckChange?.(e.target.checked)}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
            />
          )}

          <Box flex={1}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography
                variant="subtitle1"
                fontWeight={800}
                sx={{ textTransform: 'capitalize' }}
              >
                {item.itemName}
              </Typography>
              <Chip size="small" label={sInfo.displayText} color={sInfo.color} />
            </Stack>

            <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap">
              <Chip size="small" label={`x${item.noOfItems} ${item.quantityType}`} />
              {item.quantity > 1 && (
                <Chip size="small" label={`${item.quantity} ${item.quantityType}`} />
              )}
              {item.tableNo && <Chip size="small" label={`Table #${item.tableNo}`} />}
              {item.billNo && <Chip size="small" label={`Bill #${item.billNo}`} />}
            </Stack>

              <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap">
              <Typography
                variant="subtitle1"
                fontWeight={800}
                sx={{ textTransform: 'capitalize' }}
              >
                {`Received: ${new Date(item.kitchInfo?.updatedAt || "").toLocaleTimeString()}`}
              </Typography>
              </Stack>

            {instructions && (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: 'action.hover',
                  border: (t) => `1px dashed ${t.palette.divider}`,
                }}
              >
                <Typography variant="caption" fontWeight={800} sx={{ mr: 0.5 }}>
                  Instructions:
                </Typography>
                <Typography variant="body2" component="span" sx={{ whiteSpace: 'normal' }}>
                  {instructions}
                </Typography>
              </Box>
            )}

            {/* Action */}
            {onAdvance && (
              <Stack direction="row" spacing={1.25} mt={1.25}>
                <Button
                  onClick={onAdvance}
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  sx={{ minHeight: 48, px: 2.25 }}
                >
                  {advanceLabel}
                </Button>
              </Stack>
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};
